import React, { useState, useEffect, useCallback } from "react";
import {
  makeStyles,
  useTheme,
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Switch,
  Tooltip,
  CircularProgress,
  Chip,
  Divider,
  useMediaQuery,
} from "@material-ui/core";
import {
  DescriptionRounded as PageIcon,
  SaveRounded as SaveIcon,
  CheckCircleRounded as PublishedIcon,
  RadioButtonUncheckedRounded as DraftIcon,
  InfoOutlined as InfoIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

// ─── Supported language tabs ─────────────────────────────────────────────────
const LANGUAGES = [
  { code: "he", label: "עברית", dir: "rtl" },
  { code: "en", label: "English", dir: "ltr" },
  { code: "ru", label: "Русский", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
  { code: "es", label: "Español", dir: "ltr" },
];

// ─── Default pages seeded by migration ───────────────────────────────────────
const DEFAULT_PAGES = [
  { slug: "terms-of-service",  labelKey: "sitePages.slugs.termsOfService" },
  { slug: "privacy-policy",    labelKey: "sitePages.slugs.privacyPolicy" },
  { slug: "about-us",          labelKey: "sitePages.slugs.aboutUs" },
  { slug: "cookie-policy",     labelKey: "sitePages.slugs.cookiePolicy" },
];

const META_TITLE_MAX     = 70;
const META_DESC_MAX      = 160;

// ─── Design tokens (CRM language — copy from Tags/Campaigns/Contacts) ────────
const SP  = { xxs:4, xs:8, sm:12, md:16, lg:20, xl:24, xxl:32, hero:48 };
const TT  = { min:44, primary:48, hero:56 };
const RAD = { pill:999, sm:8, md:12, lg:16, xl:20 };
const COL = {
  brandGreen:'#25d366', brandGreenDeep:'#128c7e',
  brandGreenSoft:'rgba(37,211,102,0.08)',
  textPrimary:'#0B0D0C', textSecondary:'#52525b',
  borderSoft:'#eaeaea', cardBg:'#ffffff',
  destructive:'#ef4444'
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => {
  const isDark     = theme.palette.type === "dark";
  const accent     = isDark ? "#2ee37a" : "#128c7e";
  const accentSoft = isDark ? "rgba(46,227,122,0.08)" : "rgba(18,140,126,0.06)";
  const hair       = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.06)";
  const fg         = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
  const fgMuted    = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,22,20,0.55)";

  return {
  root: {
    display: "flex",
    gap: 0,
    minHeight: 540,
    border: `1px solid ${hair}`,
    borderRadius: 18,
    overflow: "hidden",
    background: isDark ? "#13181a" : "#ffffff",
    boxShadow: isDark
      ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 14px 36px rgba(0,0,0,0.32)"
      : "0 1px 0 rgba(15,22,20,0.02), 0 8px 24px rgba(15,22,20,0.04)",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    // Below sm — flip side-by-side into vertical stack so each section
    // gets full viewport width. Without this 220 sidebar + form sums past
    // phone width, squishing inputs to ~100px and bleeding past the edge.
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      minHeight: 0,
      borderRadius: 14,
    },
    // iOS Safari zooms inputs <16px font-size on focus. Force 16px floor
    // on xs across every input so viewport never jumps when tapping fields.
    [theme.breakpoints.down("xs")]: {
      "& .MuiInputBase-input": { fontSize: 16 },
      // Bottom padding under sticky save bar so last field doesn't sit
      // underneath the bar.
      paddingBottom: 0,
    },
  },
  sidebar: {
    width: 240,
    borderInlineEnd: `1px solid ${hair}`,
    background: isDark ? "rgba(255,255,255,0.02)" : "#fbfbfa",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    paddingTop: SP.md,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      borderInlineEnd: "none",
      borderBottom: `1px solid ${hair}`,
      flexShrink: 1,
      background: "transparent",
      paddingTop: SP.sm,
    },
    // xs: become a vertical Linear-dense card stack
    [theme.breakpoints.down("xs")]: {
      padding: `8px 8px 16px`,
      gap: 6,
    },
  },
  // Section eyebrow — uppercase 10.5px / 0.22em / accent green.
  // Identical rhythm to Tags table head + mobileTagMeta.
  sidebarHeading: {
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.22em",
    color: accent,
    padding: `0 ${SP.md}px ${SP.sm}px`,
    [theme.breakpoints.down("xs")]: {
      padding: `${SP.xxs}px ${SP.xs}px ${SP.xs}px`,
    },
  },
  // ── Desktop sidebar row ────────────────────────────────────────────
  sidebarItem: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px 12px 18px",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    color: fg,
    borderRadius: 0,
    transition: "background 0.14s ease, color 0.14s ease",
    "&:hover": { background: accentSoft, color: fg },
    // ── Mobile: Linear-dense card per page (xs override) ──────────
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "auto 1fr auto",
      alignItems: "flex-start",
      columnGap: 10,
      padding: `10px 12px`,
      boxSizing: "border-box",
      background: isDark ? "#13181a" : "#ffffff",
      borderRadius: 8,
      border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
      boxShadow: "none",
      transition: "border-color 0.14s ease, box-shadow 0.14s ease",
      minHeight: 0,
      fontSize: 13,
      fontWeight: 500,
      // Remove the accent stripe — Linear-dense uses a 6-8px status dot
      // rendered via the published/draft dot element instead.
      "&::before": {
        content: "none",
        display: "none",
      },
      "&:hover": {
        background: isDark ? "#13181a" : "#ffffff",
        borderColor: isDark ? "rgba(255,255,255,0.16)" : "#d4d4d4",
        boxShadow: "0 1px 2px rgba(10,10,10,0.04)",
      },
      "&:active": {
        background: isDark ? "rgba(255,255,255,0.02)" : "#fafafa",
      },
    },
  },
  // ── Mobile body wrapper — name + slug meta ───────────────────────
  sidebarItemBody: {
    display: "none",
    [theme.breakpoints.down("xs")]: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
      minWidth: 0,
      gridColumn: "2 / 3",
    },
  },
  sidebarItemName: {
    [theme.breakpoints.down("xs")]: {
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1.35,
      color: isDark ? "rgba(255,255,255,0.92)" : "#18181b",
      letterSpacing: "0em",
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "clip",
      wordBreak: "normal",
      overflowWrap: "anywhere",
      display: "block",
    },
  },
  sidebarItemSlug: {
    [theme.breakpoints.down("xs")]: {
      fontSize: 10,
      fontWeight: 400,
      letterSpacing: "0em",
      textTransform: "none",
      color: isDark ? "rgba(255,255,255,0.45)" : "#71717a",
      overflow: "visible",
      textOverflow: "clip",
      whiteSpace: "normal",
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
    },
  },
  // Desktop-only label (hidden on xs, replaced by sidebarItemBody)
  sidebarItemLabel: {
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  // Selected state — desktop: brand-tint background, accent left bar.
  // Mobile (xs): 1px accent border (no double border), soft accent wash,
  // no heavy shadow.
  sidebarItemActive: {
    background: accentSoft,
    color: fg,
    fontWeight: 600,
    boxShadow: `inset 3px 0 0 ${accent}`,
    "&:hover": { background: accentSoft, color: fg },
    [theme.breakpoints.down("xs")]: {
      background: isDark ? "rgba(46,227,122,0.08)" : "rgba(37,211,102,0.06)",
      borderColor: accent,
      borderWidth: 1,
      padding: `10px 12px`,
      boxShadow: "none",
      "&::before": {
        content: "none",
        display: "none",
      },
      "&:hover": {
        background: isDark ? "rgba(46,227,122,0.08)" : "rgba(37,211,102,0.06)",
        borderColor: accent,
      },
    },
  },
  publishedDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: COL.brandGreen,
    boxShadow: `0 0 0 3px ${isDark ? "rgba(46,227,122,0.12)" : "rgba(37,211,102,0.12)"}`,
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      width: 6,
      height: 6,
      background: "#25d366",
      boxShadow: "none",
      marginTop: 6,
      alignSelf: "flex-start",
      gridColumn: "1 / 2",
    },
  },
  draftDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "transparent",
    border: `1.5px solid ${fgMuted}`,
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      width: 6,
      height: 6,
      background: isDark ? "rgba(255,255,255,0.25)" : "#a3a3a3",
      border: "none",
      boxShadow: "none",
      marginTop: 6,
      alignSelf: "flex-start",
      gridColumn: "1 / 2",
    },
  },
  editorArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  // ── Editor header — accent meta eyebrow + page name + actions ─────
  editorHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: `${SP.lg}px ${SP.xl}px`,
    borderBottom: `1px solid ${hair}`,
    gap: SP.md,
    flexWrap: "wrap",
    minWidth: 0,
    background: isDark ? "rgba(255,255,255,0.01)" : "#fdfdfc",
    [theme.breakpoints.down("xs")]: {
      padding: `${SP.md}px ${SP.md}px`,
      gap: SP.sm,
    },
  },
  editorTitle: {
    display: "flex",
    alignItems: "flex-start",
    gap: SP.sm,
    minWidth: 0,
    flex: "1 1 auto",
    "& > svg": { color: accent, fontSize: 22, marginTop: 2 },
    [theme.breakpoints.down("xs")]: {
      "& > svg": { color: accent, fontSize: 18, marginTop: 1 },
    },
  },
  editorTitleText: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
  },
  // Accent eyebrow above page name — uppercase 10.5/0.22em #128c7e
  accentMeta: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    color: accent,
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  pageNameText: {
    fontSize: 20,
    fontWeight: 600,
    color: fg,
    letterSpacing: "-0.2px",
    lineHeight: 1.3,
    [theme.breakpoints.down("xs")]: {
      fontSize: 17,
      fontWeight: 600,
    },
  },
  // Slug badge — retained for desktop title row context (small inline)
  slugBadge: {
    fontSize: 11,
    color: fgMuted,
    background: accentSoft,
    borderRadius: RAD.sm,
    padding: "3px 8px",
    fontFamily: "monospace",
    display: "inline-block",
    marginTop: 4,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: SP.md,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      flexWrap: "wrap",
      gap: SP.sm,
      justifyContent: "flex-start",
    },
  },
  publishedRow: {
    display: "flex",
    alignItems: "center",
    gap: SP.xs,
    fontSize: 13,
    fontWeight: 500,
    color: fgMuted,
    padding: `6px 10px 6px 8px`,
    borderRadius: RAD.pill,
    border: `1px solid ${hair}`,
    background: isDark ? "rgba(255,255,255,0.02)" : "#ffffff",
  },
  // Desktop save button — pill, hairline shadow, brand green
  saveBtn: {
    background: COL.brandGreen,
    color: "#fff",
    fontWeight: 600,
    borderRadius: RAD.pill,
    padding: "8px 22px",
    fontSize: 14,
    textTransform: "none",
    letterSpacing: 0,
    boxShadow: "0 1px 0 rgba(15,22,20,0.04), 0 6px 16px rgba(37, 211, 102, 0.24)",
    "&:hover": {
      background: COL.brandGreenDeep,
    },
    "&.Mui-disabled": { opacity: 0.5, background: COL.brandGreen, color: "#fff" },
    // On xs the header save is hidden; sticky bar takes over (see below).
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  // ── Sticky bottom save bar (xs only) ─────────────────────────────
  stickySaveBar: {
    display: "none",
    [theme.breakpoints.down("xs")]: {
      display: "flex",
      alignItems: "center",
      gap: SP.sm,
      position: "sticky",
      bottom: 0,
      insetInline: 0,
      zIndex: 6,
      padding: `${SP.sm}px ${SP.md}px calc(${SP.sm}px + env(safe-area-inset-bottom, 0px))`,
      background: isDark ? "rgba(19,24,26,0.96)" : "rgba(255,255,255,0.96)",
      backdropFilter: "saturate(180%) blur(12px)",
      WebkitBackdropFilter: "saturate(180%) blur(12px)",
      borderTop: `1px solid ${hair}`,
      boxShadow: isDark
        ? "0 -8px 24px rgba(0,0,0,0.32)"
        : "0 -8px 24px rgba(15,22,20,0.04)",
    },
  },
  stickySaveButton: {
    flex: 1,
    background: COL.brandGreen,
    color: "#fff",
    fontWeight: 600,
    borderRadius: RAD.md,
    height: TT.primary,
    fontSize: 15,
    textTransform: "none",
    letterSpacing: 0,
    boxShadow: "0 1px 0 rgba(15,22,20,0.04), 0 6px 16px rgba(37,211,102,0.24)",
    "&:hover": { background: COL.brandGreenDeep },
    "&.Mui-disabled": { opacity: 0.55, background: COL.brandGreen, color: "#fff" },
  },
  langTabs: {
    borderBottom: `1px solid ${hair}`,
    background: isDark ? "rgba(255,255,255,0.01)" : "#fdfdfc",
    minHeight: 44,
    "& .MuiTabs-flexContainer": {
      paddingInline: SP.md,
    },
    "& .MuiTab-root": {
      minWidth: 0,
      minHeight: 44,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: fgMuted,
      padding: `10px ${SP.md}px`,
      flexShrink: 0,
    },
    "& .Mui-selected": { color: accent },
    "& .MuiTabs-indicator": { background: accent, height: 2 },
  },
  formArea: {
    flex: 1,
    padding: `${SP.xl}px ${SP.xl}px`,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: SP.lg,
    background: isDark ? "transparent" : "#ffffff",
    [theme.breakpoints.down("xs")]: {
      padding: `${SP.md}px ${SP.md}px ${SP.lg}px`,
      gap: SP.md,
    },
  },
  // Section group — accent meta + field block, separated by hairline
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: SP.xs,
    paddingBottom: SP.md,
    borderBottom: `1px solid ${hair}`,
    "&:last-child": { borderBottom: "none", paddingBottom: 0 },
  },
  fieldGroupHeader: {
    display: "flex",
    alignItems: "center",
    gap: SP.xs,
    marginBottom: 2,
  },
  fieldBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  // Field label — now small accent-meta caption (10.5/0.22em)
  fieldLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    color: accent,
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 4,
  },
  charCount: {
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: 0.4,
    color: fgMuted,
    marginInlineStart: "auto",
    fontFeatureSettings: '"tnum" 1',
  },
  charCountWarn: {
    color: COL.destructive,
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: RAD.md,
      fontSize: 14,
      background: isDark ? "rgba(255,255,255,0.02)" : "#ffffff",
      "& fieldset": { borderColor: hair },
      "&:hover fieldset": { borderColor: accent },
      "&.Mui-focused fieldset": { borderColor: accent, borderWidth: 1 },
    },
  },
  bodyField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: RAD.md,
      fontSize: 13.5,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      background: isDark ? "rgba(255,255,255,0.02)" : "#fbfbfa",
      "& fieldset": { borderColor: hair },
      "&:hover fieldset": { borderColor: accent },
      "&.Mui-focused fieldset": { borderColor: accent, borderWidth: 1 },
    },
  },
  // Empty state — unified with CRM (64px 0, 10.5 meta, 14 desc)
  emptyState: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 14,
    color: fgMuted,
    padding: "56px 24px 32px",
    textAlign: "center",
    "& > svg": { fontSize: 56, color: accent, opacity: 0.5 },
  },
  emptyStateMeta: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    color: accent,
  },
  emptyStateDesc: {
    fontSize: 14,
    lineHeight: 1.5,
    color: fgMuted,
    maxWidth: 280,
    margin: 0,
  },
  };
});

// ─── Helper: build blank translation object ───────────────────────────────────
const blankLangData = () => ({ title: "", metaTitle: "", metaDescription: "", body: "" });
const blankAllLangs = () =>
  LANGUAGES.reduce((acc, l) => { acc[l.code] = blankLangData(); return acc; }, {});

// ─── Main Component ───────────────────────────────────────────────────────────
const SitePagesManager = () => {
  const classes  = useStyles();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const [pages, setPages]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [langTab, setLangTab]       = useState(0);
  const [saving, setSaving]         = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  // translations[slug][langCode] = { title, metaTitle, metaDescription, body }
  const [translations, setTranslations] = useState({});
  // published status per slug
  const [publishedMap, setPublishedMap] = useState({});

  // ── Load pages ─────────────────────────────────────────────────────────────
  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/pages");
      const rawPages = data.pages || [];

      // Merge with defaults so unknown slugs still appear in sidebar
      const slugSet = new Set(rawPages.map((p) => p.slug));
      const merged = [
        ...rawPages,
        ...DEFAULT_PAGES.filter((d) => !slugSet.has(d.slug)).map((d) => ({
          slug: d.slug,
          published: false,
          translations: [],
        })),
      ];

      setPages(merged);

      const transMap = {};
      const pubMap   = {};
      merged.forEach((page) => {
        pubMap[page.slug] = Boolean(page.published);
        transMap[page.slug] = blankAllLangs();
        (page.translations || []).forEach((t) => {
          if (transMap[page.slug][t.language] !== undefined) {
            transMap[page.slug][t.language] = {
              title:           t.title           ?? "",
              metaTitle:       t.metaTitle        ?? "",
              metaDescription: t.metaDescription  ?? "",
              body:            t.body             ?? "",
            };
          }
        });
      });

      setTranslations(transMap);
      setPublishedMap(pubMap);
      if (!selectedSlug && merged.length > 0) {
        setSelectedSlug(merged[0].slug);
      }
    } catch (err) {
      toast.error(i18n.t("sitePages.loadError", { defaultValue: "Failed to load pages" }));
    } finally {
      setLoading(false);
    }
  }, [selectedSlug]);

  useEffect(() => { fetchPages(); }, []); // eslint-disable-line

  // ── Field change ───────────────────────────────────────────────────────────
  const handleFieldChange = (slug, lang, field, value) => {
    setTranslations((prev) => ({
      ...prev,
      [slug]: {
        ...prev[slug],
        [lang]: { ...prev[slug][lang], [field]: value },
      },
    }));
  };

  // ── Save translations ──────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedSlug) return;
    setSaving(true);
    try {
      await api.put(`/api/admin/pages/${selectedSlug}`, {
        translations: translations[selectedSlug],
      });
      toast.success(i18n.t("sitePages.saveSuccess", { defaultValue: "Page saved" }));
    } catch (err) {
      toast.error(i18n.t("sitePages.saveError", { defaultValue: "Save failed" }));
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle published ───────────────────────────────────────────────────────
  const handleTogglePublished = async (slug) => {
    const next = !publishedMap[slug];
    setStatusSaving(true);
    try {
      await api.patch(`/api/admin/pages/${slug}/status`, { published: next });
      setPublishedMap((prev) => ({ ...prev, [slug]: next }));
      toast.success(
        next
          ? i18n.t("sitePages.published", { defaultValue: "Page published" })
          : i18n.t("sitePages.unpublished", { defaultValue: "Page set to draft" })
      );
    } catch {
      toast.error(i18n.t("sitePages.statusError", { defaultValue: "Status update failed" }));
    } finally {
      setStatusSaving(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const currentLang     = LANGUAGES[langTab];
  const currentTrans    = selectedSlug && translations[selectedSlug]
    ? translations[selectedSlug][currentLang.code] || blankLangData()
    : null;
  const isPublished     = selectedSlug ? Boolean(publishedMap[selectedSlug]) : false;

  const labelForSlug = (slug) => {
    const def = DEFAULT_PAGES.find((d) => d.slug === slug);
    return def ? i18n.t(def.labelKey, { defaultValue: slug }) : slug;
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={6}>
        <CircularProgress size={32} style={{ color: "#25d366" }} />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      <Box className={classes.sidebar}>
        <Typography className={classes.sidebarHeading}>
          {i18n.t("sitePages.sidebarHeading", { defaultValue: "Pages" })}
        </Typography>

        {pages.map((page, idx) => (
          <Box
            key={page.slug}
            role="button"
            tabIndex={0}
            className={`${classes.sidebarItem} ${selectedSlug === page.slug ? classes.sidebarItemActive : ""}`}
            style={{ "--i": idx }}
            onClick={() => setSelectedSlug(page.slug)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setSelectedSlug(page.slug);
            }}
          >
            {/* Desktop: simple inline label */}
            <span className={classes.sidebarItemLabel}>{labelForSlug(page.slug)}</span>
            {/* Mobile (xs): name + slug accent meta stack */}
            <Box className={classes.sidebarItemBody}>
              <span className={classes.sidebarItemName}>{labelForSlug(page.slug)}</span>
              <span className={classes.sidebarItemSlug}>{page.slug}</span>
            </Box>
            <span
              className={
                publishedMap[page.slug] ? classes.publishedDot : classes.draftDot
              }
            />
          </Box>
        ))}
      </Box>

      {/* ── Editor ──────────────────────────────────────────────────── */}
      <Box className={classes.editorArea}>
        {!selectedSlug ? (
          <Box className={classes.emptyState}>
            <PageIcon />
            <Typography component="div" className={classes.emptyStateMeta}>
              {i18n.t("sitePages.emptyMeta", { defaultValue: "No page selected" })}
            </Typography>
            <Typography component="p" className={classes.emptyStateDesc}>
              {i18n.t("sitePages.selectPage", { defaultValue: "Select a page from the list to edit its translations." })}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Header row */}
            <Box className={classes.editorHeader}>
              <Box className={classes.editorTitle}>
                <PageIcon />
                <Box className={classes.editorTitleText}>
                  <span className={classes.accentMeta}>
                    {i18n.t("sitePages.eyebrowEditing", { defaultValue: "Editing page" })}
                  </span>
                  <Typography component="div" className={classes.pageNameText}>
                    {labelForSlug(selectedSlug)}
                  </Typography>
                  <span className={classes.slugBadge}>{selectedSlug}</span>
                </Box>
              </Box>

              <Box className={classes.headerActions}>
                {/* Published toggle */}
                <Box className={classes.publishedRow}>
                  {isPublished ? (
                    <PublishedIcon style={{ fontSize: 16, color: "#22c55e" }} />
                  ) : (
                    <DraftIcon style={{ fontSize: 16, color: "#d4d4d8" }} />
                  )}
                  <span>
                    {isPublished
                      ? i18n.t("sitePages.statusPublished", { defaultValue: "Published" })
                      : i18n.t("sitePages.statusDraft", { defaultValue: "Draft" })}
                  </span>
                  <Tooltip
                    title={
                      isPublished
                        ? i18n.t("sitePages.setDraft", { defaultValue: "Set to draft" })
                        : i18n.t("sitePages.publish", { defaultValue: "Publish page" })
                    }
                  >
                    <span>
                      <Switch
                        checked={isPublished}
                        onChange={() => handleTogglePublished(selectedSlug)}
                        disabled={statusSaving}
                        size="small"
                        color="primary"
                      />
                    </span>
                  </Tooltip>
                </Box>

                {/* Save button */}
                <Button
                  variant="contained"
                  className={classes.saveBtn}
                  startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                  disabled={saving}
                  onClick={handleSave}
                  disableElevation
                >
                  {saving
                    ? i18n.t("sitePages.saving", { defaultValue: "Saving…" })
                    : i18n.t("sitePages.save", { defaultValue: "Save" })}
                </Button>
              </Box>
            </Box>

            {/* Language tabs */}
            <Tabs
              value={langTab}
              onChange={(_, v) => setLangTab(v)}
              className={classes.langTabs}
            >
              {LANGUAGES.map((l, idx) => (
                <Tab key={l.code} label={l.label} id={`lang-tab-${idx}`} />
              ))}
            </Tabs>

            {/* Form */}
            {currentTrans && (
              <Box
                className={classes.formArea}
                dir={currentLang.dir}
              >
                {/* Title */}
                <Box className={classes.fieldGroup}>
                  <Box className={classes.fieldBlock}>
                    <Typography component="div" className={classes.fieldLabel}>
                      {i18n.t("sitePages.fieldTitle", { defaultValue: "Page Title" })}
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      className={classes.textField}
                      value={currentTrans.title}
                      onChange={(e) =>
                        handleFieldChange(selectedSlug, currentLang.code, "title", e.target.value)
                      }
                      inputProps={{ dir: currentLang.dir, style: { fontSize: 16 } }}
                    />
                  </Box>
                </Box>

                {/* Meta Title */}
                <Box className={classes.fieldGroup}>
                  <Box className={classes.fieldBlock}>
                    <Typography component="div" className={classes.fieldLabel}>
                      {i18n.t("sitePages.fieldMetaTitle", { defaultValue: "Meta Title" })}
                      <Tooltip title={i18n.t("sitePages.metaTitleHint", { defaultValue: "Max 70 characters. Used by Google as the page title in search results." })}>
                        <InfoIcon style={{ fontSize: 14, color: "#a1a1aa", cursor: "help" }} />
                      </Tooltip>
                      <span
                        className={`${classes.charCount} ${
                          currentTrans.metaTitle.length > META_TITLE_MAX ? classes.charCountWarn : ""
                        }`}
                      >
                        {currentTrans.metaTitle.length} / {META_TITLE_MAX}
                      </span>
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      className={classes.textField}
                      value={currentTrans.metaTitle}
                      onChange={(e) =>
                        handleFieldChange(selectedSlug, currentLang.code, "metaTitle", e.target.value)
                      }
                      inputProps={{ maxLength: META_TITLE_MAX + 20, dir: currentLang.dir, style: { fontSize: 16 } }}
                      error={currentTrans.metaTitle.length > META_TITLE_MAX}
                      helperText={
                        currentTrans.metaTitle.length > META_TITLE_MAX
                          ? i18n.t("sitePages.tooLong", { defaultValue: "Too long — Google will truncate" })
                          : ""
                      }
                    />
                  </Box>
                </Box>

                {/* Meta Description */}
                <Box className={classes.fieldGroup}>
                  <Box className={classes.fieldBlock}>
                    <Typography component="div" className={classes.fieldLabel}>
                      {i18n.t("sitePages.fieldMetaDesc", { defaultValue: "Meta Description" })}
                      <Tooltip title={i18n.t("sitePages.metaDescHint", { defaultValue: "Max 160 characters. Shown under the title in Google search results." })}>
                        <InfoIcon style={{ fontSize: 14, color: "#a1a1aa", cursor: "help" }} />
                      </Tooltip>
                      <span
                        className={`${classes.charCount} ${
                          currentTrans.metaDescription.length > META_DESC_MAX ? classes.charCountWarn : ""
                        }`}
                      >
                        {currentTrans.metaDescription.length} / {META_DESC_MAX}
                      </span>
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      multiline
                      rows={3}
                      className={classes.textField}
                      value={currentTrans.metaDescription}
                      onChange={(e) =>
                        handleFieldChange(selectedSlug, currentLang.code, "metaDescription", e.target.value)
                      }
                      inputProps={{ maxLength: META_DESC_MAX + 20, dir: currentLang.dir, style: { fontSize: 16 } }}
                      error={currentTrans.metaDescription.length > META_DESC_MAX}
                      helperText={
                        currentTrans.metaDescription.length > META_DESC_MAX
                          ? i18n.t("sitePages.tooLong", { defaultValue: "Too long — Google will truncate" })
                          : ""
                      }
                    />
                  </Box>
                </Box>

                {/* Body */}
                <Box className={classes.fieldGroup}>
                  <Box className={classes.fieldBlock}>
                    <Typography component="div" className={classes.fieldLabel}>
                      {i18n.t("sitePages.fieldBody", { defaultValue: "Page Body (HTML)" })}
                      <Tooltip title={i18n.t("sitePages.bodyHint", { defaultValue: "Full HTML content of the page. Rendered as-is on whapiz.com." })}>
                        <InfoIcon style={{ fontSize: 14, color: "#a1a1aa", cursor: "help" }} />
                      </Tooltip>
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      multiline
                      rows={14}
                      className={classes.bodyField}
                      value={currentTrans.body}
                      onChange={(e) =>
                        handleFieldChange(selectedSlug, currentLang.code, "body", e.target.value)
                      }
                      inputProps={{ dir: currentLang.dir, style: { fontSize: 16 } }}
                      placeholder="<h1>Title</h1>\n<p>Content...</p>"
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/* Sticky bottom save bar (xs only) — full-width primary CTA
                with hairline top border per CRM pattern. Mirrors header
                Save button; header version is hidden on xs. */}
            {currentTrans && isMobile && (
              <Box className={classes.stickySaveBar}>
                <Button
                  variant="contained"
                  className={classes.stickySaveButton}
                  startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                  disabled={saving}
                  onClick={handleSave}
                  disableElevation
                  fullWidth
                >
                  {saving
                    ? i18n.t("sitePages.saving", { defaultValue: "Saving…" })
                    : i18n.t("sitePages.save", { defaultValue: "Save changes" })}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default SitePagesManager;
